"use client";

import React, { useCallback, useRef } from "react";
import { SearchField } from "@repo/ui-parkmobile/SearchField";
import {
  SearchFieldWithAutocomplete,
  PlacePrediction,
} from "./SearchFieldWithAutocomplete";
import { useGoogleMaps } from "@/providers/GoogleMapsProvider";
import { createParkingSearchUrl } from "@/utils/parkingSearch";

interface SearchFieldWrapperProps {
  placeholder?: string;
  className?: string;
  onSearch?: (value: string) => void;
  sectionName?: string;
  analyticsEvent?: "search_location" | "";
}

export function SearchFieldWrapper({
  placeholder = "Search by location or venue",
  className,
  onSearch,
  sectionName,
  analyticsEvent,
}: SearchFieldWrapperProps) {
  const { isLoaded, placesService } = useGoogleMaps();
  const placeDetailsCache = useRef<
    Map<string, { lat: number; lng: number; zoom?: number }>
  >(new Map());

  const calculateZoomFromViewport = (viewport: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): number => {
    const latDiff = Math.abs(viewport.north - viewport.south);
    const lngDiff = Math.abs(viewport.east - viewport.west);
    const maxDiff = Math.max(latDiff, lngDiff);
    const zoom = Math.round(Math.log2(360 / maxDiff)) - 1;
    return Math.max(6, Math.min(18, zoom));
  };

  const getPlaceDetails = useCallback(
    (
      placeId: string
    ): Promise<{ lat: number; lng: number; zoom?: number } | null> => {
      const cached = placeDetailsCache.current.get(placeId);
      if (cached) {
        return Promise.resolve(cached);
      }

      return new Promise((resolve) => {
        if (!window.google?.maps?.places?.PlacesService) {
          resolve(null);
          return;
        }

        const map = new window.google.maps.Map(document.createElement("div"));
        const service = new window.google.maps.places.PlacesService(map);

        const request = {
          placeId: placeId,
          fields: ["geometry"],
        };

        service.getDetails(request, (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place?.geometry?.location
          ) {
            const result: { lat: number; lng: number; zoom?: number } = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };

            if (place.geometry.viewport) {
              const viewport = {
                north: place.geometry.viewport.getNorthEast().lat(),
                south: place.geometry.viewport.getSouthWest().lat(),
                east: place.geometry.viewport.getNorthEast().lng(),
                west: place.geometry.viewport.getSouthWest().lng(),
              };
              result.zoom = calculateZoomFromViewport(viewport);
            }

            placeDetailsCache.current.set(placeId, result);
            resolve(result);
          } else {
            resolve(null);
          }
        });
      });
    },
    []
  );

  const handlePlaceSelect = useCallback(
    async (place: PlacePrediction) => {
      const isCityOnly =
        place.types &&
        (place.types.includes("locality") ||
          (place.types.includes("sublocality") &&
            !place.types.includes("establishment")));

      if (!isCityOnly) {
        await getPlaceDetails(place.place_id);
      }
    },
    [getPlaceDetails]
  );

  const getSearchHref = useCallback(
    (place: PlacePrediction | null, inputValue: string): string | undefined => {
      if (!inputValue.trim()) {
        return undefined;
      }

      if (!place) {
        return createParkingSearchUrl(inputValue);
      }

      const isCityOnly =
        place.types &&
        (place.types.includes("locality") ||
          (place.types.includes("sublocality") &&
            !place.types.includes("establishment")));

      if (isCityOnly) {
        return createParkingSearchUrl(place.description);
      }

      const cached = placeDetailsCache.current.get(place.place_id);
      if (cached) {
        return createParkingSearchUrl(place.description, {
          coordinates: cached,
          zoom: cached.zoom || 16,
        });
      }

      return createParkingSearchUrl(place.description);
    },
    []
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (onSearch) {
        onSearch(value);
      } else {
        const url = createParkingSearchUrl(value);
        window.location.href = url;
      }
    },
    [onSearch]
  );

  if (isLoaded && placesService) {
    return (
      <SearchFieldWithAutocomplete
        placeholder={placeholder}
        className={className}
        onPlaceSelect={handlePlaceSelect}
        onSearch={handleSearch}
        getSearchHref={getSearchHref}
        searchOptions={{}}
        sectionName={sectionName}
        analyticsEvent={analyticsEvent}
      />
    );
  }

  return (
    <SearchField
      placeholder={placeholder}
      className={className}
      onSearch={(value) => value.trim() && handleSearch(value)}
    />
  );
}
